import React, { useEffect, useRef } from 'react';
import './MagicBento.css';
import { gsap } from 'gsap';
import PifLogo from './PifLogo';
import LexusLogo from './LexusLogo';
import RolexLogo from './RolexLogo';

const MagicBento = ({ tournamentLogoHours, insightsHours, pifHours, lexusHours, rolexHours }) => {
  const bentoRef = useRef(null);

  useEffect(() => {
    if (bentoRef.current) {
      initializeAnimations();
    }
  }, [tournamentLogoHours, insightsHours, pifHours, lexusHours, rolexHours]);

  const initializeAnimations = () => {
    const cards = bentoRef.current.querySelectorAll('.card');
    
    // Initial animation
    gsap.fromTo(cards, {
      opacity: 0,
      y: 50,
      scale: 0.8
    }, {
      opacity: 1,
      y: 0,
      scale: 1,
      duration: 0.8,
      stagger: 0.2,
      ease: "back.out(1.7)"
    });

    // Add hover animations
    cards.forEach(card => {
      card.addEventListener('mouseenter', (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        
        const rotateX = ((y - centerY) / centerY) * -6;
        const rotateY = ((x - centerX) / centerX) * 6;
        
        gsap.to(e.currentTarget, {
          rotateX,
          rotateY,
          duration: 0.1,
          ease: "power2.out",
          transformPerspective: 1000,
        });
      });

      card.addEventListener('mouseleave', (e) => {
        gsap.to(e.currentTarget, {
          rotateX: 0,
          rotateY: 0,
          duration: 0.3,
          ease: "power2.out",
        });
      });

      card.addEventListener('click', (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const maxDistance = Math.max(
          Math.hypot(x, y),
          Math.hypot(x - rect.width, y),
          Math.hypot(x, y - rect.height),
          Math.hypot(x - rect.width, y - rect.height)
        );

        const ripple = document.createElement("div");
        ripple.style.cssText = `
          position: absolute;
          width: ${maxDistance * 2}px;
          height: ${maxDistance * 2}px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(227, 242, 58, 0.4) 0%, rgba(227, 242, 58, 0.2) 30%, transparent 70%);
          left: ${x - maxDistance}px;
          top: ${y - maxDistance}px;
          pointer-events: none;
          z-index: 1000;
        `;

        e.currentTarget.appendChild(ripple);

        gsap.fromTo(
          ripple,
          { scale: 0, opacity: 1 },
          { scale: 1, opacity: 0, duration: 0.8, ease: "power2.out", onComplete: () => ripple.remove() }
        );
      });
    });
  };

  return (
    <div className="magic-bento" ref={bentoRef}>
      <div className="card-grid">
        <div className="card card--border-glow">
          <div className="card__content">
            <div className="card__title">
              <div style={{ height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', fontSize: '1.6rem', fontWeight: '600', color: '#FFFFFF' }}>Tournament Logo</div>
            </div>
            <div className="card__time-section">
              <p className="card__subtitle">Total On-Screen Time</p>
              <div className="card__description">{tournamentLogoHours}</div>
            </div>
          </div>
        </div>
        
        <div className="card card--border-glow">
          <div className="card__content">
            <div className="card__title">
              <div style={{ height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', fontSize: '1.6rem', fontWeight: '600', color: '#FFFFFF' }}>Insights</div>
            </div>
            <div className="card__time-section">
              <p className="card__subtitle">Total On-Screen Time</p>
              <div className="card__description">{insightsHours}</div>
            </div>
          </div>
        </div>
        
        <div className="card card--border-glow">
          <div className="card__content">
            <div className="card__title">
              <PifLogo />
            </div>
            <div className="card__time-section">
              <p className="card__subtitle">Total On-Screen Time</p>
              <div className="card__description">{pifHours}</div>
            </div>
          </div>
        </div>
        
        <div className="card card--border-glow lexus-card">
          <div className="card__content">
            <div className="card__title">
              <LexusLogo />
            </div>
            <div className="card__time-section">
              <p className="card__subtitle">Total On-Screen Time</p>
              <div className="card__description">{lexusHours}</div>
            </div>
          </div>
        </div>

        <div className="card card--border-glow rolex-card">
          <div className="card__content">
            <div className="card__title">
              <RolexLogo />
            </div>
            <div className="card__time-section">
              <p className="card__subtitle">Total On-Screen Time</p>
              <div className="card__description">{rolexHours}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MagicBento; 